import {IDiagnosis} from '../_interface/diagnosis.model';
import {IDiagnosisIdNode} from '../_interface/diagnosisid-node.interface';
import {setTreeNode} from './setTreeNode.fn';
import {getFlatTreeNode} from './getFlatTreeNode.fn';
import {cloneDeep, set} from 'lodash-es';

export interface TraverseNode {
	diagnosisId: string;
	parentId: string;
	disabled: boolean;
}
export const recreateTree = (diagnosis: IDiagnosis, inputTree: IDiagnosisIdNode, fTree: Record<string, string[]>)
	: {tree: IDiagnosisIdNode, flatTree: Record<string, string[]>} => {
	const parent = diagnosis.parentId;
	const ftNode = getFlatTreeNode(diagnosis.diagnosisId, parent, fTree);
	const flatTree = {...fTree, ...ftNode};
	const tree = setTreeNode(diagnosis.diagnosisId, flatTree[diagnosis.diagnosisId], inputTree);
	return {flatTree, tree};
};

export const recreateTreeMulti = (
	diagnosis: IDiagnosis[],
	inputTree: IDiagnosisIdNode,
	flat: Record<string, string[]>
): { tree: IDiagnosisIdNode; flatTree: Record<string, string[]> } => {
	const diagnosisSortedByCode = sortDiagnosisByCode(diagnosis);
	const traverseNodes: TraverseNode[] = diagnosisSortedByCode.map(({ diagnosisId, parentId, disabled }) => ({
		diagnosisId,
		parentId,
		disabled
	}));
	const tempTree: IDiagnosisIdNode = cloneDeep(inputTree);
	const tempFlat: Record<string, string[]> = { ...flat };

	const knownIds = new Set(traverseNodes.map(n => n.diagnosisId));

	const singleIteration = (nodes: TraverseNode[]) => {
		const leftover: TraverseNode[] = [];

		for (const node of nodes) {
			if (!node.parentId) {
				tempFlat[node.diagnosisId] = null;
				set(tempTree, [node.diagnosisId], null);
				continue;
			}

			if (tempFlat[node.parentId] !== undefined) {
				tempFlat[node.diagnosisId] = [...(tempFlat[node.parentId] || []), node.parentId];
				set(tempTree, [...tempFlat[node.diagnosisId], node.diagnosisId], null);
				continue;
			}

			if (!knownIds.has(node.parentId)) {
				continue;
			}

			leftover.push(node);
		}
		if (leftover.length && leftover.length !== nodes.length) {
			singleIteration(leftover);
		}
	};

	singleIteration(traverseNodes);

	return { tree: tempTree, flatTree: tempFlat };
};

const sortDiagnosisByCode = (diagnosis: IDiagnosis[]): IDiagnosis[] => {
	if (!diagnosis) return [];
	const rootsWithCode: IDiagnosis[] = diagnosis.length === 1 ? diagnosis : [...diagnosis]
		.filter(d => !d.parentId)
		.filter(d => !!d.code);
	const rootsWithNoCode: IDiagnosis[] = diagnosis.length === 1 ? diagnosis : [...diagnosis]
		.filter(d => !d.parentId)
		.filter(d => !d.code);
	const childrenWithCode: IDiagnosis[] = diagnosis.length === 1 ? diagnosis : [...diagnosis]
		.filter(d => d.parentId)
		.filter(d => !!d.code);
	const childrenWithNoCode: IDiagnosis[] = diagnosis.length === 1 ? diagnosis : [...diagnosis]
		.filter(d => d.parentId)
		.filter(d => !d.code);
	const rootsSortedByCode = rootsWithCode.sort((a, b) => a.code.localeCompare(b.code)).concat([...rootsWithNoCode]);
	const childrenSortedByCode = childrenWithCode.sort((a, b) => a.code.localeCompare(b.code)).concat([...childrenWithNoCode]);
	return [...rootsSortedByCode, ...childrenSortedByCode];
};
